import React, { useEffect, useRef } from 'react';
import { Link } from '@remix-run/react';
import { Button as RoccoButton } from '@rocco/components/button';
import type { FilterTabViewModel } from '@rocco/ui/filter-sort/view-models/filter-tab';
import { DisplayMode } from '@rocco/types/enum/display-mode';

interface FilterTabsProps {
    tabs: FilterTabViewModel[];
    displayMode?: DisplayMode;
    tabContainerRef?: React.RefObject<HTMLDivElement>;
    onTabClick?: (tab: FilterTabViewModel) => void;
    preventScrollReset?: boolean;
}

export const FilterTabs = ({
    tabs,
    tabContainerRef,
    onTabClick,
    preventScrollReset = true,
}: FilterTabsProps) => {
    const activeTabRef = useRef<HTMLButtonElement>(null);
    const activeTabIndex = tabs.findIndex((tab) => tab.selected);

    useEffect(() => {
        // Only scroll if we have a container ref (search page specific)
        if (!tabContainerRef?.current || !activeTabRef.current) {
            return;
        }

        const scrollToActiveTab = () => {
            const container = tabContainerRef.current;
            const activeTab = activeTabRef.current;

            if (!container || !activeTab) return;

            // Calculate the position to center the active tab
            const containerWidth = container.clientWidth;
            const tabLeft = activeTab.offsetLeft;
            const tabWidth = activeTab.offsetWidth;

            // Center the tab in the container
            const scrollPosition = tabLeft - containerWidth / 2 + tabWidth / 2;

            container.scrollTo({
                left: Math.max(0, scrollPosition), // Don't scroll to negative positions
                behavior: 'smooth',
            });
        };

        // Scroll immediately
        scrollToActiveTab();

        // Also scroll after a delay to handle any layout changes
        const timeout = setTimeout(scrollToActiveTab, 200);

        return () => clearTimeout(timeout);
    }, [tabContainerRef, activeTabIndex, tabs.length]); // Add tabs.length to trigger on tab changes

    return tabs.length > 0 ? (
        <div className="flex items-center gap-[0.375rem] bg-background md:gap-2">
            {tabs.map((tab) => (
                <RoccoButton
                    key={tab.id}
                    color={tab.selected ? 'dark' : 'secondary'}
                    ref={tab.selected ? activeTabRef : null}
                    onClick={() => onTabClick?.(tab)}
                    asChild
                >
                    <Link
                        className="tracking-0 md:tracking-0 text-sm leading-[1.625rem] md:text-base"
                        to={tab.link}
                        preventScrollReset={preventScrollReset}
                    >
                        {tab.title}
                    </Link>
                </RoccoButton>
            ))}
        </div>
    ) : null;
};
